/**
 * 생육 분석 장비 고르기 — 농장별로 묶은 드롭다운 (장비가 수백 개여도 한 줄).
 * 옛 '측정 기록' 탭의 장비 선택을 그대로 옮겨 3D 분석과 함께 쓴다.
 */
import React from 'react';
import type { DeviceInfo, FarmInfo } from '@grosync/common';
import { ModelTag, StatusPill, Sym, type DeviceStatus as UiDeviceStatus } from '../../../ui';

export function toUiStatus(s: DeviceInfo['status']): UiDeviceStatus {
  switch (s) {
    case 'ACTIVE':
      return 'ok';
    case 'WARNING':
    case 'ERROR':
      return 'warn';
    default:
      return 'off';
  }
}

const optionLabel = (d: DeviceInfo): string =>
  `${d.name} · ${d.id}${toUiStatus(d.status) === 'off' ? ' (오프라인)' : ''}`;

export function GrowthDeviceSelect({
  devices,
  farms,
  value,
  onChange,
}: {
  devices: DeviceInfo[];
  farms: FarmInfo[];
  value: string;
  onChange: (id: string) => void;
}): React.ReactElement {
  const farmIds = new Set(farms.map((f) => f.id));
  const unassigned = devices.filter((d) => !d.farmId || !farmIds.has(d.farmId));
  const device = devices.find((d) => d.id === value);
  const farm = farms.find((f) => f.id === device?.farmId);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <Sym name="eco" size={18} fill={1} style={{ color: 'var(--md-sys-color-primary)' }} />
        <span style={{ font: '600 13px/1 var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)', whiteSpace: 'nowrap' }}>
          생육 장비
        </span>
        <select
          aria-label="생육 장비"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{
            height: 38,
            padding: '0 12px',
            borderRadius: 10,
            cursor: 'pointer',
            border: '1px solid var(--md-sys-color-outline-variant)',
            background: 'var(--md-sys-color-surface-container-low)',
            font: '600 14px/1 var(--gs-font-kr)',
            color: 'var(--md-sys-color-on-surface)',
            outline: 'none',
            minWidth: 280,
            maxWidth: '100%',
          }}
        >
          {farms.map((f) => {
            const ds = devices.filter((d) => d.farmId === f.id);
            if (!ds.length) return null;
            return (
              <optgroup key={f.id} label={f.name}>
                {ds.map((d) => (
                  <option key={d.id} value={d.id}>{optionLabel(d)}</option>
                ))}
              </optgroup>
            );
          })}
          {unassigned.length > 0 && (
            <optgroup label="미할당">
              {unassigned.map((d) => (
                <option key={d.id} value={d.id}>{optionLabel(d)}</option>
              ))}
            </optgroup>
          )}
        </select>
      </div>
      {device && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <StatusPill s={toUiStatus(device.status)} />
          <span style={{ font: '400 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
            {farm?.name ?? '미할당'}
            {device.location?.description ? ` · ${device.location.description}` : ''}
          </span>
          <ModelTag m={device.model ?? 1} />
        </div>
      )}
    </div>
  );
}
